/**
 * What language a country suggests — and where it refuses to guess.
 *
 * Cloudflare tells us which country a request came from. That is a fact about
 * a network, not about a person: it is the strongest hint available for
 * somebody who has told us nothing yet, and it is wrong for every traveller,
 * every expatriate and everybody on a VPN. So nothing here is ever applied as
 * a decision. It seeds a default in a field the person can change, and it
 * offers a suggestion the person can decline.
 *
 * A country is listed only when one of the languages we support is the
 * dominant everyday written language there. Countries that are genuinely
 * split — Belgium, Switzerland, India, the Philippines, Afghanistan — are
 * deliberately absent, because on those a guess is not better than silence:
 * it is a wrong answer delivered with confidence to roughly half the country.
 */
import { isValidLanguage } from './constants.ts';

const BY_LANGUAGE: Readonly<Record<string, readonly string[]>> = Object.freeze({
  ar: ['SA', 'AE', 'EG', 'DZ', 'MA', 'TN', 'LY', 'SD', 'IQ', 'JO', 'KW', 'LB', 'OM', 'QA', 'BH', 'YE', 'SY', 'PS', 'MR'],
  bg: ['BG'],
  bn: ['BD'],
  cs: ['CZ'],
  da: ['DK'],
  de: ['DE', 'AT', 'LI'],
  el: ['GR', 'CY'],
  en: ['US', 'GB', 'AU', 'NZ', 'IE', 'CA', 'SG', 'ZA', 'NG', 'GH', 'KE', 'UG', 'ZM', 'ZW', 'BW', 'MW', 'JM', 'TT', 'BS', 'BB', 'BZ', 'GY', 'MT'],
  es: ['ES', 'MX', 'AR', 'CO', 'PE', 'VE', 'CL', 'EC', 'GT', 'CU', 'BO', 'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'GQ'],
  fa: ['IR'],
  fi: ['FI'],
  fr: ['FR', 'MC', 'SN', 'CI', 'ML', 'BF', 'NE', 'TG', 'BJ', 'GA', 'CG', 'CF', 'GN'],
  he: ['IL'],
  hu: ['HU'],
  id: ['ID'],
  it: ['IT', 'SM', 'VA'],
  ja: ['JP'],
  km: ['KH'],
  ko: ['KR', 'KP'],
  lo: ['LA'],
  ms: ['MY', 'BN'],
  my: ['MM'],
  ne: ['NP'],
  nl: ['NL', 'SR'],
  no: ['NO', 'SJ'],
  pl: ['PL'],
  pt: ['PT', 'BR', 'AO', 'MZ', 'CV', 'GW', 'ST'],
  ro: ['RO', 'MD'],
  ru: ['RU', 'BY', 'KZ', 'KG'],
  si: ['LK'],
  sk: ['SK'],
  sv: ['SE', 'AX'],
  sw: ['TZ'],
  th: ['TH'],
  tr: ['TR'],
  uk: ['UA'],
  ur: ['PK'],
  vi: ['VN'],
  zh: ['CN'],
  'zh-TW': ['TW', 'HK', 'MO'],
});

/** Inverted once, at module load, so a lookup is a map read. */
const BY_COUNTRY: ReadonlyMap<string, string> = new Map(
  Object.entries(BY_LANGUAGE).flatMap(([language, countries]) =>
    countries.map((country) => [country, language] as const),
  ),
);

/**
 * The language a country suggests, or null where we decline to guess.
 *
 * Null is a real answer and the caller must handle it: it means "this country
 * does not have one written language we can name", which is true of a great
 * many of them.
 */
export function languageForCountry(country: string | null | undefined): string | null {
  if (!country) return null;
  const language = BY_COUNTRY.get(country.trim().toUpperCase());
  // Guards the table against a language being removed from LANGUAGE_CODES and
  // this file not being updated with it.
  return language !== undefined && isValidLanguage(language) ? language : null;
}

/** Every country the table has an opinion about. Exported for tests. */
export function countriesWithALanguage(): readonly string[] {
  return [...BY_COUNTRY.keys()];
}
